import {useEffect, useState} from 'react';
import {UserPublicInfo} from '../backend/responses/user-public-info';
import {UsersRepository} from '../repositories/users';

export type UsersInfoCache = {
  [userId: string]: UserPublicInfo;
};

/**
 * Custom hook to cache user information based on user IDs.
 * It fetches user public info data and caches it.
 */
export function useUsersInfoCache(userIds?: (string | undefined)[]) {
  const [users, setUsers] = useState<UsersInfoCache>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userIds || (userIds && userIds.length === 0)) {
      setLoading(false);
      return;
    }

    let requests: Promise<UserPublicInfo>[] = [];
    let requestIds: string[] = [];

    for (const userId of userIds) {
      // Don't request users that are already in the cache
      if (userId && !requestIds.includes(userId)) {
        requestIds.push(userId);
        requests.push(UsersRepository.instance.getInfoById(userId));
      }
    }

    if (requests.length > 0) {
      setLoading(true);
      Promise.allSettled<Promise<UserPublicInfo>[]>(requests).then((results) => {
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const user = result.value;
            setUsers((prev) => {
              if (prev[user.id] === user) return prev;
              return {...prev, [user.id]: user};
            });
          }
        });
        setLoading(false);
      });
    }
  }, [userIds]);

  return {users, loading};
}
